import { TrendingUp } from 'lucide-react'
import { cn } from '@/utils/cn'
import type { PortalData } from '@/types/portalCliente'
import type { NpsStats } from '@/utils/portalClienteUtils'
import { getHealthColor, getHealthLabel } from '@/utils/portalClienteUtils'

type Props = { data: PortalData; npsStats: NpsStats }

export function PortalClienteHealthSection({ data, npsStats }: Props) {
  /** Portal mantém escala visual 0–100 (espelho do final 0–18). */
  const score = data.cliente.healthScore
  const finalScore = data.cliente.healthScoreFinal

  return (
    <section className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5 space-y-4">
      <h2 className="text-xs font-bold text-accent uppercase tracking-wider flex items-center gap-2">
        <TrendingUp size={14} />
        Health Score
      </h2>
      <div className="flex items-center gap-6">
        <div className="relative w-20 h-20">
          <svg className="w-20 h-20 -rotate-90" viewBox="0 0 80 80">
            <circle cx="40" cy="40" r="34" fill="none" stroke="white" strokeWidth="5" opacity="0.06" />
            <circle
              cx="40"
              cy="40"
              r="34"
              fill="none"
              stroke="currentColor"
              strokeWidth="5"
              strokeDasharray={`${(score / 100) * 213.6} 213.6`}
              className={cn(
                score >= 70 ? 'text-emerald-500' : score >= 40 ? 'text-yellow-500' : 'text-red-500',
              )}
              strokeLinecap="round"
            />
          </svg>
          <span
            className={cn(
              'absolute inset-0 flex items-center justify-center text-2xl font-bold',
              score >= 70 ? 'text-emerald-400' : score >= 40 ? 'text-yellow-400' : 'text-red-400',
            )}
          >
            {finalScore != null ? finalScore : score}
          </span>
        </div>
        <div className="flex-1 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-white">
              Score Geral{finalScore != null ? ` (${finalScore}/18)` : ''}
            </span>
            <span
              className={cn(
                'text-[11px] font-bold px-2 py-0.5 rounded-full',
                score >= 70
                  ? 'bg-emerald-500/20 text-emerald-400'
                  : score >= 40
                    ? 'bg-yellow-500/20 text-yellow-400'
                    : 'bg-red-500/20 text-red-400',
              )}
            >
              {getHealthLabel(score)}
            </span>
          </div>
          <div className="h-2.5 bg-white/[0.06] rounded-full overflow-hidden">
            <div
              className={cn('h-full rounded-full transition-all duration-700', getHealthColor(score))}
              style={{ width: `${score}%` }}
            />
          </div>
          <div className="flex items-center justify-between text-[11px]">
            <span className="text-white/40">0</span>
            <span className="text-white/40">
              Cliente desde {new Date(data.cliente.dataInicio).toLocaleDateString('pt-BR')}
            </span>
            <span className="text-white/40">{finalScore != null ? '18' : '100'}</span>
          </div>
        </div>
      </div>
      {data.nps.length > 0 && (
        <div className="grid grid-cols-3 gap-3 pt-3 border-t border-white/[0.06]">
          <div className="text-center p-3 rounded-lg bg-white/[0.02]">
            <p className="text-[10px] text-white/40 uppercase tracking-wider">NPS Score</p>
            <p
              className={cn(
                'text-lg font-bold',
                npsStats.nps >= 50 ? 'text-emerald-400' : npsStats.nps >= 0 ? 'text-yellow-400' : 'text-red-400',
              )}
            >
              {npsStats.nps >= 0 ? '+' : ''}
              {npsStats.nps}
            </p>
          </div>
          <div className="text-center p-3 rounded-lg bg-white/[0.02]">
            <p className="text-[10px] text-white/40 uppercase tracking-wider">Média</p>
            <p className="text-lg font-bold text-white">{npsStats.media.toFixed(1)}</p>
          </div>
          <div className="text-center p-3 rounded-lg bg-white/[0.02]">
            <p className="text-[10px] text-white/40 uppercase tracking-wider">Respostas</p>
            <p className="text-lg font-bold text-white">{npsStats.total}</p>
          </div>
        </div>
      )}
    </section>
  )
}
